import { OptimizerResult, SearchSettings } from '../types/optimizer';
import type { SearchProgress } from './engine';
import type { CardLibrary } from './cardLibrary';

const cache = new Map<string, OptimizerResult>();

/** A separate worker per search makes cancellation immediate, even during synchronous scoring. */
export function optimizeInBrowser(chartId: string, settings: SearchSettings, options: {
    signal: AbortSignal; onProgress: (progress: SearchProgress) => void; library?: CardLibrary;
}): Promise<OptimizerResult> {
    const key = JSON.stringify([chartId, Object.entries(settings).sort(([a], [b]) => a.localeCompare(b)), options.library ?? null]);
    if (options.signal.aborted) return Promise.reject(new DOMException('Search cancelled', 'AbortError'));
    const cached = cache.get(key);
    if (cached) {
        cache.delete(key);
        cache.set(key, cached);
        return Promise.resolve(cached);
    }
    return new Promise((resolve, reject) => {
        let worker: Worker;
        try {
            worker = new Worker(new URL('./optimizer.worker.ts', import.meta.url));
        } catch (err) {
            reject(err);
            return;
        }
        const cleanup = () => {
            options.signal.removeEventListener('abort', cancel);
            worker.terminate();
        };
        const cancel = () => {
            cleanup();
            reject(new DOMException('Search cancelled', 'AbortError'));
        };
        options.signal.addEventListener('abort', cancel, { once: true });
        worker.onerror = event => {
            cleanup();
            reject(new Error(event.message || 'Unable to start the optimizer worker'));
        };
        worker.onmessage = event => {
            const message = event.data;
            if (message.type === 'progress') options.onProgress(message.progress);
            else if (message.type === 'error') {
                cleanup();
                reject(new Error(message.error));
            } else if (message.type === 'result') {
                cleanup();
                if (message.result?.schema_version !== 1 || message.result.chart_id !== chartId) {
                    reject(new Error('Unexpected optimizer result'));
                    return;
                }
                cache.set(key, message.result);
                while (cache.size > 32) cache.delete(cache.keys().next().value!);
                resolve(message.result);
            }
        };
        // Workers resolve relative fetch URLs from their own script directory.
        // Resolve the site's asset root on the page before handing it to the worker.
        const publicUrl = new URL(`${(process.env.PUBLIC_URL || '.').replace(/\/$/, '')}/`, document.baseURI).href;
        worker.postMessage({ chartId, settings, publicUrl, library: options.library });
    });
}
